/**
 * Grounded answer renderer — the digestion stack: a confidence-badged header, an
 * "In short" TL;DR callout, a short bulleted body where each point ends with its
 * citation markers, and an optional caveat quote block. Citation markers are small
 * clickable pills; clicking one calls `onCite` (the workspace opens the drawer at
 * that chunk + activates the matching source card).
 */
import { Badge } from "@d12labs/ui/badge";
import { Copy, Quote, RefreshCw, Sparkles } from "lucide-react";
import { CONFIDENCE_META, tokenizeRichText } from "../kb-ui";
import type { KbAnswer, KbCitation } from "../types";

/** Inline citation marker — a small superscript pill; filled when active. */
function Cite({ n, active, onClick }: { n: number; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={`Source ${n}`}
			className={`mx-px inline-flex h-[15px] min-w-[15px] -translate-y-0.5 items-center justify-center rounded px-1 align-baseline text-[10px] font-semibold transition-colors ${
				active
					? "bg-primary text-on-primary"
					: "bg-primary/15 text-primary-hover hover:bg-primary/30"
			}`}
		>
			{n}
		</button>
	);
}

/** Render the citation pills for a point/caveat from its excerpt numbers. */
function CiteMarkers({
	ns,
	citations,
	activeCite,
	onCite,
}: {
	ns: number[];
	citations: KbCitation[];
	activeCite: number | null;
	onCite: (c: KbCitation) => void;
}) {
	return (
		<>
			{ns.map((n) => {
				const c = citations.find((x) => x.n === n);
				if (!c) return null;
				return <Cite key={n} n={n} active={activeCite === n} onClick={() => onCite(c)} />;
			})}
		</>
	);
}

/** Render answer text with **bold** runs (citations are appended separately). */
function RichText({ text }: { text: string }) {
	return (
		<>
			{tokenizeRichText(text).map((seg, i) =>
				seg.kind === "bold" ? (
					// biome-ignore lint/suspicious/noArrayIndexKey: stable token order within a fixed string
					<b key={i} className="font-semibold text-foreground">
						{seg.text}
					</b>
				) : seg.kind === "text" ? (
					// biome-ignore lint/suspicious/noArrayIndexKey: stable token order within a fixed string
					<span key={i}>{seg.text}</span>
				) : null,
			)}
		</>
	);
}

export function Answer({
	answer,
	activeCite,
	onCite,
	onRegenerate,
}: {
	answer: KbAnswer;
	activeCite: number | null;
	onCite: (c: KbCitation) => void;
	onRegenerate?: () => void;
}) {
	const conf = CONFIDENCE_META[answer.confidence];
	const lead = answer.citations[0];

	const copyAnswer = () => {
		const lines = [
			answer.tldr,
			"",
			...answer.points.map((p) => `• ${p.text} ${p.citations.map((n) => `[${n}]`).join("")}`),
			...(answer.caveat ? ["", `Note: ${answer.caveat.text}`] : []),
			"",
			"Sources:",
			...answer.citations.map((c) => `[${c.n}] ${c.filename} (chunk #${c.chunkIndex})`),
		];
		navigator.clipboard?.writeText(lines.join("\n")).catch(() => {});
	};

	return (
		<div>
			<div className="mb-2 flex items-center justify-between">
				<span className="text-eyebrow uppercase text-ink-subtle">Answer</span>
				<div className="flex items-center gap-2 text-meta text-ink-subtle">
					<Badge tone={conf.tone}>{conf.label}</Badge>
					<span>·</span>
					<span>
						{answer.citations.length} source{answer.citations.length === 1 ? "" : "s"}
					</span>
				</div>
			</div>

			{/* TL;DR callout */}
			<div className="mb-4 rounded-lg border border-primary/25 bg-primary/[0.07] p-3.5">
				<div className="mb-1 flex items-center gap-1.5 text-primary-hover">
					<Sparkles className="size-3.5" />
					<span className="text-eyebrow uppercase">In short</span>
				</div>
				<p className="text-body-lg leading-relaxed text-foreground">
					<RichText text={answer.tldr} />
					{lead ? (
						<Cite n={lead.n} active={activeCite === lead.n} onClick={() => onCite(lead)} />
					) : null}
				</p>
			</div>

			{/* Supporting points */}
			{answer.points.length > 0 ? (
				<ul className="space-y-2.5 text-body-lg leading-relaxed text-ink-muted">
					{answer.points.map((p, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: stable point order within a fixed answer
						<li key={i} className="flex gap-2.5">
							<span className="mt-2 size-1.5 flex-none rounded-full bg-primary/60" />
							<div className="flex-1">
								<RichText text={p.text} />{" "}
								<CiteMarkers
									ns={p.citations}
									citations={answer.citations}
									activeCite={activeCite}
									onCite={onCite}
								/>
							</div>
						</li>
					))}
				</ul>
			) : null}

			{/* Caveat */}
			{answer.caveat ? (
				<p className="mt-3 rounded-md border-l-2 border-warning/50 bg-surface-1 px-3 py-2 text-body-sm text-ink-subtle">
					<Quote className="mr-1 inline size-3 text-warning" />
					<RichText text={answer.caveat.text} />{" "}
					<CiteMarkers
						ns={answer.caveat.citations}
						citations={answer.citations}
						activeCite={activeCite}
						onCite={onCite}
					/>
				</p>
			) : null}

			{/* Actions */}
			<div className="mt-4 flex items-center gap-3 text-meta text-ink-subtle">
				<button
					type="button"
					onClick={copyAnswer}
					className="inline-flex items-center gap-1.5 hover:text-foreground"
				>
					<Copy className="size-3" /> Copy
				</button>
				{onRegenerate ? (
					<button
						type="button"
						onClick={onRegenerate}
						className="inline-flex items-center gap-1.5 hover:text-foreground"
					>
						<RefreshCw className="size-3" /> Regenerate
					</button>
				) : null}
			</div>
		</div>
	);
}
